import * as React from "react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replohq/lattice/components/tabs/Tabs";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import { AgentBillingTab } from "@v2/features/admin/tabs/AgentBillingTab";
import { FeatureFlagsTab } from "@v2/features/admin/tabs/FeatureFlags";
import { MiscellaneousTab } from "@v2/features/admin/tabs/Miscellaneous";
import { OptionsTab } from "@v2/features/admin/tabs/Options";
import { QuickIdsTab } from "@v2/features/admin/tabs/QuickIds";
import { FloatingPanel } from "@v2/features/debug/FloatingPanel";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";

const AdminPanelInner = () => {
  const isDebugModeAllowed = useIsDebugMode();

  if (!isDebugModeAllowed) {
    return null;
  }

  return (
    <Tabs
      defaultValue="feature-flags"
      className="min-h-0 flex-1"
      UNSAFE_className="text-xs"
    >
      <TabsList variant="outline" size="sm">
        <TabsTrigger size="sm" value="feature-flags" UNSAFE_className="text-xs">
          Feature Flags
        </TabsTrigger>
        <TabsTrigger size="sm" value="options" UNSAFE_className="text-xs">
          Options
        </TabsTrigger>
        <TabsTrigger size="sm" value="miscellaneous" UNSAFE_className="text-xs">
          Miscellaneous
        </TabsTrigger>
        <TabsTrigger size="sm" value="agent-billing" UNSAFE_className="text-xs">
          Subscriptions
        </TabsTrigger>
        <TabsTrigger size="sm" value="quick-ids" UNSAFE_className="text-xs">
          Quick IDs
        </TabsTrigger>
      </TabsList>
      <TabsContent
        value="feature-flags"
        className="flex min-h-0 flex-1"
        UNSAFE_className="flex-col overflow-y-auto p-3"
      >
        <FeatureFlagsTab />
      </TabsContent>
      <TabsContent
        value="options"
        className="flex min-h-0 flex-1"
        UNSAFE_className="flex-col overflow-y-auto p-3"
      >
        <OptionsTab />
      </TabsContent>
      <TabsContent
        value="miscellaneous"
        className="flex min-h-0 flex-1"
        UNSAFE_className="flex-col gap-1 overflow-y-auto p-3"
      >
        <MiscellaneousTab />
      </TabsContent>
      <TabsContent
        value="agent-billing"
        className="flex min-h-0 flex-1"
        UNSAFE_className="flex-col overflow-y-auto p-3"
      >
        <AgentBillingTab />
      </TabsContent>
      <TabsContent
        value="quick-ids"
        className="flex min-h-0 flex-1"
        UNSAFE_className="flex-col overflow-y-auto p-3"
      >
        <QuickIdsTab />
      </TabsContent>
    </Tabs>
  );
};

export const AdminPanel = () => {
  const { close } = useAdminPanelActions();
  const [isMinimized, setIsMinimized] = useLocalStorageState(
    "replo.debug.adminPanel.minimized",
    false,
    { validate: (value): value is boolean => typeof value === "boolean" },
  );

  return (
    <FloatingPanel
      title="Admin Panel"
      storageKey="replo.debug.adminPanel"
      defaultSize={{ width: 700, height: 500 }}
      minSize={{ width: 400, height: 300 }}
      maxSize={{ width: 2000, height: 1200 }}
      onClose={close}
      onMinimize={() => setIsMinimized((prev) => !prev)}
      isMinimized={isMinimized ?? false}
    >
      <AdminPanelInner />
    </FloatingPanel>
  );
};
